/**
 * Composable for handling Intercom mode switching based on route navigation
 * 
 * Automatically switches between general support and PawSupport modes
 * when navigating to/from the PawSupport page.
 */
export const useIntercomNavigation = () => {
  const route = useRoute()
  const { switchMode, currentMode, isIntercomAvailable } = useIntercomManager()
  
  /**
   * Check if current route is the PawSupport route
   */
  const isPawSupportRoute = (routePath: string): boolean => {
    return routePath.startsWith('/pawsupport')
  }
  
  /**
   * Handle route changes and switch Intercom mode accordingly
   */
  const handleRouteChange = async (to: string, from: string | undefined) => {
    // Only proceed if Intercom is available
    if (!isIntercomAvailable.value) {
      return
    }
    
    const enteringPawSupport = isPawSupportRoute(to)
    const leavingPawSupport = from ? isPawSupportRoute(from) : false
    
    // Entering PawSupport page - switch to PawSupport mode, but don't show chat launcher
    // because user needs to check terms box before accessing PawSupport
    if (enteringPawSupport && !leavingPawSupport) {
      await switchMode('pawsupport')
    } else if (!enteringPawSupport && leavingPawSupport) {
      // Leaving PawSupport page - switch back to general support
      await switchMode('general')
    } else if (enteringPawSupport && leavingPawSupport) {
      // SPECIAL CASE: We're on PawSupport route but route watcher fired with same route
      // (this can happen due to Nuxt's route watching behavior),
      // check if we need to switch to PawSupport mode
      if (currentMode.value !== 'pawsupport') {
        await switchMode('pawsupport')
      }
    } else if (!enteringPawSupport && !leavingPawSupport) {
      // SPECIAL CASE: We're on a non-PawSupport route but route watcher shows same route
      // (this can happen due to Nuxt's route watching behavior),
      // check if we need to switch to general mode
      if (currentMode.value === 'pawsupport') {
        await switchMode('general')
      }
    }
  }
  
  /**
   * Setup route watcher
   * Call this once when the app initializes
   */
  const setupRouteWatcher = () => {
    // Watch for route changes
    watch(
      route,
      (to, from) => {
        const toPath = to.path
        const fromPath = from?.path
        
        handleRouteChange(toPath, fromPath)
      },
      {
        // Don't trigger on initial load
        immediate: false
      }
    )
  }
  
  return {
    setupRouteWatcher,
    isPawSupportRoute,
    handleRouteChange
  }
}
